import { isString } from "lodash";
import { mix, parseToRgb, rgba, toColorString } from "@glossy";

// Regex from https://github.com/styled-components/polished/blob/main/src/color/parseToRgb.js#L8
const hexRegex = /^#[a-fA-F0-9]{6}$/;
const hexRgbaRegex = /^#[a-fA-F0-9]{8}$/;
const reducedHexRegex = /^#[a-fA-F0-9]{3}$/;
const reducedRgbaHexRegex = /^#[a-fA-F0-9]{4}$/;
const rgbRegex = /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i;
const rgbaRegex = /^rgba\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*([-+]?[0-9]*[.]?[0-9]+)\s*\)$/i;
const hslRegex = /^hsl\(\s*(\d{0,3}[.]?[0-9]+)\s*,\s*(\d{1,3}[.]?[0-9]?)%\s*,\s*(\d{1,3}[.]?[0-9]?)%\s*\)$/i;
const hslaRegex = /^hsla\(\s*(\d{0,3}[.]?[0-9]+)\s*,\s*(\d{1,3}[.]?[0-9]?)%\s*,\s*(\d{1,3}[.]?[0-9]?)%\s*,\s*([-+]?[0-9]*[.]?[0-9]+)\s*\)$/i;

/**
 * Checks if given string is a color of hex format.
 */
export const isHexColor = (value: string): boolean =>
  isString(value) &&
  (!!value.match(hexRegex) ||
    !!value.match(hexRgbaRegex) ||
    !!value.match(reducedHexRegex) ||
    !!value.match(reducedRgbaHexRegex));

/**
 * Checks if given string is a color of rgb format.
 */
export const isRgbColor = (value: string): boolean =>
  isString(value) && (!!value.match(rgbRegex) || !!value.match(rgbaRegex));

/**
 * Checks if given string is a color of hsl format.
 */
export const isHslColor = (value: string): boolean =>
  isString(value) && (!!value.match(hslRegex) || !!value.match(hslaRegex));

/**
 * Checks if given string is a color of any format.
 */
export const isColor = (value: string): boolean =>
  isHexColor(value) || isRgbColor(value) || isHslColor(value);

/**
 * Mixes a color with a given amount of white
 */
export const whiten = (amount, color) => mix(amount, "#FFF", color);

/**
 * Mixes a color with a given amount of black
 */
export const blacken = (amount, color) => mix(amount, "#000", color);

/**
 * Returns a brightness amount between 0 and 1.
 * Anything greater than 0.5 should be bright enough for dark text.
 */
export const brightness = (color: string): number => {
  const { red, green, blue } = parseToRgb(color);
  return (red * 299 + green * 587 + blue * 114) / 255000;
};

/**
 * Returns a color with an absolute amount of opacity.
 */
export const fade = (color: string, opacity: number): string =>
  rgba(color, opacity / 100);

/**
 * Returns an array of colors given a list and a number of steps to match.
 */
export const gradients = (list: string[], count: number): string[] => {
  let gradients: string[] = [];
  const length = list.length;
  const step = Math.ceil(count / length) + 1;
  const rest = count + length - 2 - step * (length - 1);
  for (let cpt = 1; cpt <= length - 1; cpt++) {
    const currentStep = cpt === 1 ? rest + step : step;
    const step2 = 100 / (currentStep - 1);
    const temp = [];
    for (let i = 0; i < currentStep; i++) {
      temp.push(mix((i * step2) / 100, list[cpt], list[cpt - 1]));
    }
    gradients = gradients.concat(cpt > 1 ? temp.slice(1) : temp);
  }
  return gradients;
};

/**
 * Converts red, green and blue params to a hexadecimal color.
 */
export const rgbToHex = (red: number, green: number, blue: number): string =>
  toColorString({ red, green, blue });
